import React, { useState, useEffect, useRef } from 'react';

export function TrustSlider() {
  const items = [
    {
      title: 'Faglig stolthed & grundighed',
      quote: 'Jeg kører først videre, når maskinen virker som den skal. Punktum. Ingen halve løsninger, ingen midlertidige fiksfakserier.',
      tag: 'Kvalitetsgaranti',
      author: 'Mikki, MA Hvidevareservice',
      icon: 'fa-solid fa-medal',
    },
    {
      title: 'Færre tilbagekald & tilfredse kunder',
      quote: 'Jeg finder fejlen, reparerer ordentligt første gang og efterlader altid maskinen ren og pæn. Det giver færre tilbagekald og meget tilfredse kunder.',
      tag: 'Kundeoplevelse',
      author: 'Dokumenteret praksis',
      icon: 'fa-solid fa-thumbs-up',
    },
    {
      title: 'Altid prisoverslag før start',
      quote: 'Prisoverslag laves ALTID inden reparation starter – ingen overraskelser! Hvis reparationen ikke kan betale sig, siger jeg det ærligt.',
      tag: 'Gennemskuelighed',
      author: 'Faste priser & ærlighed',
      icon: 'fa-solid fa-file-invoice-dollar',
    },
    {
      title: '1–2 års garanti',
      quote: 'Jeg giver 1–2 års garanti på udført arbejde og nye originale eller kvalitetsgodkendte reservedele.',
      tag: 'Tryghed',
      author: 'Garantidækning',
      icon: 'fa-solid fa-shield-halved',
    },
    {
      title: 'Grøn omtanke & genbrug',
      quote: 'Hver reparation sparer ressourcer, metal, plastik og CO₂ i forhold til at producere en ny maskine. Det gavner både din pengepung og kloden.',
      tag: 'Bæredygtighed',
      author: 'Miljø & besparelse',
      icon: 'fa-solid fa-leaf',
    },
    {
      title: 'Hurtig udrykning',
      quote: 'Ofte hjemmebesøg inden for 1 dag. Du kontaktes altid 20 minutter før vi ankommer til din adresse.',
      tag: 'Lokal service',
      author: 'Brønshøj & Storkøbenhavn',
      icon: 'fa-solid fa-clock',
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [columns, setColumns] = useState(3);
  const touchStartX = useRef(null);

  // Responsive column count: 3 on desktop (>=1024), 2 on tablet (>=640), 1 on mobile
  useEffect(() => {
    function updateColumns() {
      if (window.innerWidth >= 1024) {
        setColumns(3);
      } else if (window.innerWidth >= 640) {
        setColumns(2);
      } else {
        setColumns(1);
      }
    }
    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  const maxIndex = Math.max(0, items.length - columns);

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 40) {
      nextSlide();
    } else if (diff < -40) {
      prevSlide();
    }
    touchStartX.current = null;
  };

  return (
    <section className="py-16 sm:py-20 bg-slate-900 text-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">
              Hvorfor kunderne vælger os
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={prevSlide}
              aria-label="Forrige dias"
              className="p-2 text-slate-400 hover:text-white transition-colors cursor-pointer"
            >
              <i className="fa-solid fa-chevron-left text-base" aria-hidden="true" />
            </button>
            <button
              onClick={nextSlide}
              aria-label="Næste dias"
              className="p-2 text-slate-400 hover:text-white transition-colors cursor-pointer"
            >
              <i className="fa-solid fa-chevron-right text-base" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Carousel Window */}
        <div
          className="relative overflow-hidden select-none"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="flex transition-transform duration-300 ease-out"
            style={{
              transform: `translateX(-${currentIndex * (100 / columns)}%)`,
            }}
          >
            {items.map((item, idx) => (
              <div
                key={idx}
                className="shrink-0 px-3"
                style={{ width: `${100 / columns}%` }}
              >
                <div className="h-full rounded-xl p-6 sm:p-7">
                  <div className="flex items-center gap-1.5 mb-4 text-white">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className="w-5 h-5 shrink-0"
                        viewBox="0 0 100 100"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="7"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        aria-hidden="true"
                      >
                        <path d="M 50 5 C 52 28, 72 48, 95 50 C 72 52, 52 72, 50 95 C 48 72, 28 52, 5 50 C 28 48, 48 28, 50 5 Z" />
                      </svg>
                    ))}
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-3">
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-300 leading-relaxed italic">
                    "{item.quote}"
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center items-center gap-2 mt-8">
          {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              aria-label={`Gå til dias ${idx + 1}`}
              className={`h-2 rounded-full transition-all duration-200 cursor-pointer ${
                currentIndex === idx ? 'w-6 bg-sky-500' : 'w-2 bg-slate-700 hover:bg-slate-600'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default TrustSlider;
